import { useEffect, useState } from "react";
import { FieldPath, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { UserClientLinkFormValues } from "@/components/admin/UserClientLinkTable/EditUserClientLinkForm";
import { type ProductConfigForEstimatedPriceUpdate } from "@/components/admin/UserTable/PricePreference/schema";
import { cn } from "@/lib/utils";

interface CheckboxGroupProps {
  title: string;
  basePath: string;
  products: ProductConfigForEstimatedPriceUpdate["products"];
  contracts: ProductConfigForEstimatedPriceUpdate["contracts"];
  disabled?: boolean;
  showLongHorizonSwitch?: boolean;
}

export const ProductPricePreferenceCheckboxGroup = ({
  title,
  basePath,
  products,
  contracts,
  disabled,
  showLongHorizonSwitch,
}: CheckboxGroupProps) => {
  const { t } = useTranslation();
  const { watch, setValue, getValues } = useFormContext<UserClientLinkFormValues>();
  const [allSelected, setAllSelected] = useState(false);
  const [isLongHorizon, setIsLongHorizon] = useState(
    getValues(`${basePath}.IS_LONG_HORIZON` as FieldPath<UserClientLinkFormValues>) || false,
  );

  const values = products.flatMap((product) =>
    contracts.map((contract) =>
      watch(`${basePath}.${product.id}.${contract.id}` as FieldPath<UserClientLinkFormValues>),
    ),
  );

  // Updates allSelected state when checkboxes change
  useEffect(() => {
    setAllSelected(values.every(Boolean));
  }, [values]);

  const toggleAll = (checked: boolean) => {
    setAllSelected(checked);
    products.forEach((product) => {
      contracts.forEach((contract) => {
        const path = `${basePath}.${product.id}.${contract.id}` as FieldPath<UserClientLinkFormValues>;
        setValue(path, checked, {
          shouldDirty: true,
          shouldTouch: true,
        });
      });
    });
  };

  const toggleLongHorizon = (checked: boolean) => {
    setIsLongHorizon(checked);
    setValue(`${basePath}.IS_LONG_HORIZON` as FieldPath<UserClientLinkFormValues>, checked, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{t(title)}</h3>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">{t("Select all")}</span>
          <Switch checked={allSelected} onCheckedChange={toggleAll} disabled={disabled} />
        </div>
      </div>
      {showLongHorizonSwitch && (
        <div className="justify-left flex items-center gap-2">
          <h4 className="text-lg font-medium">
            {t("Horizon")}: {isLongHorizon ? t("Long") : t("Short")}
          </h4>
          <Switch checked={isLongHorizon as boolean} onCheckedChange={toggleLongHorizon} />
        </div>
      )}
      {products.map((product) => (
        <div key={product.id} className="space-y-2">
          <h4 className="text-lg font-medium">{product.label}</h4>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {contracts.map(({ id, label }) => (
              <FormField<UserClientLinkFormValues>
                key={id}
                name={`${basePath}.${product.id}.${id}` as FieldPath<UserClientLinkFormValues>}
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value ? true : false}
                        onCheckedChange={field.onChange}
                        disabled={disabled}
                      />
                    </FormControl>
                    <FormLabel className={cn("font-normal", disabled && "text-muted-foreground")}>{label}</FormLabel>
                  </FormItem>
                )}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
